import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { trackEvent } from '../../analytics'
import { Toast } from '../ui/Toast'
import { API_BASE } from '../../config'

export function ContactForm() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
  })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [toastVisible, setToastVisible] = useState(false)
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setMessage('')
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setStatus('success')
        const msg = data.message || 'Thank you! We will get back to you soon.'
        setMessage(msg)
        setToastType('success')
        setToastVisible(true)
        setForm({ name: '', email: '', phone: '', company: '', service: '' })
        trackEvent('form_submit', { form_name: 'contact', form_destination: 'discovery_call' })
      } else {
        setStatus('error')
        const msg = data.message || data.errors?.[0]?.msg || 'Something went wrong. Please try again.'
        setMessage(msg)
        setToastType('error')
        setToastVisible(true)
      }
    } catch {
      setStatus('error')
      setMessage('Network error. Please try again.')
      setToastType('error')
      setToastVisible(true)
    }
  }

  const dismissToast = useCallback(() => setToastVisible(false), [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const inputClass =
    'w-full px-4 pt-5 pb-3 rounded-2xl border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-transparent focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 peer min-h-[52px]'
  const labelClass =
    'absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 transition-all duration-200 pointer-events-none px-1.5 rounded peer-focus:top-0 peer-focus:translate-y-0 peer-focus:text-accent peer-focus:text-sm peer-focus:bg-white peer-focus:dark:bg-slate-800 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-sm peer-[:not(:placeholder-shown)]:bg-white peer-[:not(:placeholder-shown)]:dark:bg-slate-800'

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="max-w-lg mx-auto space-y-4 sm:space-y-5"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="relative pt-2">
        <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder=" " className={inputClass} />
        <label className={labelClass}>Name</label>
      </div>
      <div className="relative pt-2">
        <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder=" " className={inputClass} />
        <label className={labelClass}>Email</label>
      </div>
      <div className="relative pt-2">
        <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder=" " className={inputClass} />
        <label className={labelClass}>Phone</label>
      </div>
      <div className="relative pt-2">
        <input type="text" name="company" value={form.company} onChange={handleChange} required placeholder=" " className={inputClass} />
        <label className={labelClass}>Company</label>
      </div>
      <div className="relative pt-2">
        <input
          type="text"
          name="service"
          value={form.service}
          onChange={handleChange}
          required
          placeholder=" "
          className={inputClass}
        />
        <label className={labelClass}>What your Company Do?</label>
      </div>

      <button type="submit" disabled={status === 'loading'} className="w-full btn-primary">
        {status === 'loading' ? 'Sending...' : 'Book a Discovery Call'}
      </button>

      {status === 'success' && (
        <div className="p-4 rounded-2xl bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm">
          {message}
        </div>
      )}
      {status === 'error' && (
        <div className="p-4 rounded-2xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">
          {message}
        </div>
      )}

      <Toast
        message={message}
        visible={toastVisible}
        onDismiss={dismissToast}
        type={toastType}
      />
    </motion.form>
  )
}
